import { createContext, useContext, useLayoutEffect, type ComponentType, type ReactNode } from 'react';
import type { BrandCopy } from './copy';

/** The moments a brand character can be shown at (BrandConfig.mascot). */
export type MascotMood = 'relaxed' | 'confident' | 'celebrating' | 'bemused' | 'bored' | 'confused' | 'excited';

/**
 * What makes one white-label brand different from another (ADR-0008).
 * Colours, fonts and type sizes are NOT here: they are CSS tokens in the
 * brand's theme.css. This holds labels, layout variants and feature toggles.
 */
export interface BrandConfig {
  /** Short stable id, e.g. 'tiq'. Used for analytics and test fixtures only. */
  id: string;
  name: string;
  tagline: string;
  /** The brand's logo, supplied by the brand package (it owns the image files). */
  logo: ReactNode;
  /** Label for a selection both analyses agree on, e.g. 'Prime Pick'. */
  markName: string;
  /** Name of the high-probability list, e.g. "Tippa's Picks". */
  locksName: string;
  /** Name of the long-price value list, e.g. 'Value Radar'. */
  valueName: string;
  /** Runner card layout: solid bet-colour header, or white card with a bet-colour edge. */
  runnerCard: 'header' | 'edge';
  /** Best-bet card style: light tint, or dark card with an accent strip. */
  bestBet: 'tint' | 'dark';
  /** How many upcoming tips show before "Next N". */
  upcomingShown: number;
  features: {
    highProbability: boolean;
    tour: boolean;
    /** Whether members can switch back to the classic pages (default yes). A brand that never had them says no. */
    classic?: boolean;
    /**
     * Whether the brand's website has the server-built racing-tips pages
     * (/horse-racing-tips, docs/plans/seo-public-pages.md). They are plain pages
     * outside the app, so links to them are ordinary <a href>s.
     */
    publicRacingPages?: boolean;
  };
  /** Optional landing page for signed-out visitors on the home route. */
  loggedOutHome?: ComponentType;
  /** Whether that landing page opens with the upcoming tips strip (default yes). */
  loggedOutUpcoming?: boolean;
  /** Rewording of shared headings and empty states, for a brand with its own voice (see copy.ts). */
  copy?: Partial<BrandCopy>;
  /** Optional brand character, drawn in the mood that suits the moment (a quiet day, a winner, an error). */
  mascot?: ComponentType<{ mood: MascotMood; size?: number }>;
  /**
   * Chat panel copy. Who replies (the team name) comes from the server, so the
   * panel and the reply email always agree.
   */
  chat?: {
    /** Panel heading; defaults to the team name. */
    title?: string;
    /** One line under the heading inviting a message. */
    intro: string;
  };
}

const BrandContext = createContext<BrandConfig | null>(null);

/**
 * Provides the brand and mirrors its layout variants onto <html> as data
 * attributes, so shared CSS can switch structure without naming a brand.
 */
export function BrandProvider({ brand, children }: { brand: BrandConfig; children: ReactNode }) {
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.dataset.card = brand.runnerCard;
    root.dataset.bestbet = brand.bestBet;
  }, [brand.runnerCard, brand.bestBet]);
  return <BrandContext.Provider value={brand}>{children}</BrandContext.Provider>;
}

export function useBrand(): BrandConfig {
  const brand = useContext(BrandContext);
  if (!brand) throw new Error('useBrand must be used inside <BrandProvider>');
  return brand;
}
